import React, { useRef, useState, useMemo, useEffect } from "react";
import searchIcon from "@assets/icons/search-icon.svg";
import arrowDown from "@assets/icons/arrow-down.svg";
import chevronLeft from "@assets/icons/chevron-left.svg";
import chevronRight from "@assets/icons/chevron-right.svg";
import {
  getAssignedTaskForEvaluator,
  getExamSessionByEvaluator,
  unassignEvaluationTask,
} from "@services/evaluationService";
import { subjectIDs } from "@constants/subject";
import styles from "./EvaluatorDashboard.module.css";
import Loader from "@components/loader/Loader";
import { useSelector, useDispatch } from "react-redux";
import UnAssignConfirmation from "@modals/unassignconfirmation/UnAssignConfirmation";
import { setComponentToShow } from "@store/ModalToShow";
import { useNavigate } from "react-router-dom";

function EvaluatorDashboard() {
  const componentName = useSelector(
    (state: any) => state.modalToShow.componentName
  );
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [allExamSessions, setAllExamSessions] = useState<any[]>([]);
  const [assignedTaskData, setAssignedTaskData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isLoadingAssignedTask, setIsLoadingAssignedTask] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const filteredData = useMemo(() => {
    return allExamSessions.filter((examSession) => {
      const matchesSearch =
        searchTerm === "" ||
        examSession.examSessionName
          .toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        examSession.examSessionId
          .toLowerCase()
          .includes(searchTerm.toLowerCase());
      return matchesSearch;
    });
  }, [allExamSessions, searchTerm]);
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentData = filteredData.slice(startIndex, endIndex);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, itemsPerPage]);

  const goToPage = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const handleSearch = () => {
    setCurrentPage(1);
  };

  function formatDate(dateString: string): string {
    const date = new Date(dateString);
    const day = date.getDate();
    const month = date.getMonth() + 1;
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  }

  const fetchAllExamSessions = async () => {
    setIsLoading(true);
    try {
      const res = await getExamSessionByEvaluator();
      if (res && res.exam_sessions) {
        const examSessions = res.exam_sessions.map((examSession: any) => ({
          examSessionId: examSession.id ?? "",
          examSessionName: examSession.exam_name ?? examSession.name,
          grade: examSession.grade_ids[0] ?? 0,
          subject: subjectIDs[examSession.subject_ids[0]] ?? "",
          createdOn: examSession.created_at
            ? formatDate(examSession?.created_at)
            : "",
          examDate: examSession.exam_date
            ? formatDate(examSession.exam_date)
            : "",
          questionPaperIds: examSession?.question_paper_ids ?? [],
          evaluationType: examSession.evaluation_type ?? "",
        }));
        setAllExamSessions(examSessions);
      }
    } catch (error) {
      console.error(error);
    }
    setIsLoading(false);
  };
  const fetchAssignedTasks = async () => {
    setIsLoadingAssignedTask(true);
    try {
      const res = await getAssignedTaskForEvaluator();
      if (res && res.task_id) {
        const taskData = {
          taskId: res.task_id,
          examSessionId: res.exam_submission.exam_session_id,
          evaluationType: res.evaluation_type,
          ...res,
        };
        setAssignedTaskData(taskData);
      } else {
        //Clear the state if no task is returned
        setAssignedTaskData(null);
      }
    } catch (error) {
      console.error();
    }
    setIsLoadingAssignedTask(false);
  };
  useEffect(() => {
    if (localStorage.getItem("accessToken")) {
      fetchAssignedTasks();
      fetchAllExamSessions();
    }
  }, []);
  const handleUnassignTask = async () => {
    dispatch(setComponentToShow(""));
    setIsLoading(true);
    try {
      const res = await unassignEvaluationTask();
      if (res) {
        await fetchAssignedTasks();
      }
    } catch (error) {
      console.error("Failed to unassign:", error);
    } finally {
      setIsLoading(false);
    }
  };
  const handleEvaluate = async (examSessionId: string) => {
    navigate(`/evaluator-task?exam_session_id=${examSessionId}`);
  };
  if (isLoading || isLoadingAssignedTask) {
    return <Loader />;
  }
  const renderModals = () => {
    switch (componentName) {
      case "UnAssignConfirmation":
        return (
          <UnAssignConfirmation
            isOpen={true}
            onClose={() => dispatch(setComponentToShow(""))}
            onUnAssign={handleUnassignTask}
          />
        );
      default:
        return null;
    }
  };
  return (
    <div className={styles.dashboardContainer}>
      <div className={styles.mainContent}>
        <div className={styles.header}>
          <div className={styles.title}>
            <span>Exam Sessions</span>
          </div>
        </div>
        <div className={styles.searchContainer}>
          <div className={styles.inputContainer}>
            <input
              type="text"
              placeholder="Search by Exam Name"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <img src={searchIcon} alt="Search Icon" />
          </div>
          <div className={styles.searchButtonContainer}>
            <button className={styles.searchButton} onClick={handleSearch}>
              Search
            </button>
          </div>
        </div>
        <div className={styles.testList}>
          <div>
            {assignedTaskData && (
              <div className={styles.testListTable}>
                <table>
                  <thead>
                    <tr>
                      <th>Task id</th>
                      <th></th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr key={assignedTaskData.taskId}>
                      <td>{assignedTaskData.taskId}</td>
                      <td
                        className={`${styles.testActions} ${styles.unassign}`}
                        onClick={() =>
                          dispatch(setComponentToShow("UnAssignConfirmation"))
                        }
                      >
                        unassign
                      </td>
                      <td
                        className={styles.testActions}
                        onClick={() =>
                          handleEvaluate(assignedTaskData.examSessionId)
                        }
                      >
                        resume evaluation
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}
          </div>
          <div className={styles.testListTitle}>
            <div className={styles.testTypes}></div>
            {totalPages > 0 && (
              <div className={styles.testListTitleActions}>
                <div className={styles.testListPageCountSelect}>
                  <select
                    value={itemsPerPage}
                    onChange={(e) => setItemsPerPage(Number(e.target.value))}
                  >
                    <option value="5">5</option>
                    <option value="10">10</option>
                    <option value="15">15</option>
                    <option value="20">20</option>
                  </select>
                  <img
                    src={arrowDown}
                    alt="Arrow Down Icon"
                    className={styles.arrowDownIcon}
                  />
                </div>

                <div className={styles.testListPagination}>
                  <button
                    onClick={() => goToPage(currentPage - 1)}
                    disabled={currentPage === 1}
                    className={styles.testListPaginationButton}
                  >
                    <img src={chevronLeft} alt="Previous" />
                  </button>

                  {totalPages > 0 && (
                    <>
                      <span
                        onClick={() => goToPage(1)}
                        className={`${styles.testListPaginationPageNumber} ${
                          currentPage === 1 ? styles.active : ""
                        }`}
                      >
                        1
                      </span>

                      {currentPage > 3 && (
                        <span className={styles.testListPaginationEllipsis}>
                          ...
                        </span>
                      )}

                      {currentPage > 2 && currentPage < totalPages && (
                        <span
                          onClick={() => goToPage(currentPage - 1)}
                          className={styles.testListPaginationPageNumber}
                          style={{ cursor: "pointer" }}
                        >
                          {currentPage - 1}
                        </span>
                      )}

                      {currentPage !== 1 &&
                        currentPage !== totalPages &&
                        totalPages > 1 && (
                          <span
                            className={`${styles.testListPaginationPageNumber} ${styles.active}`}
                          >
                            {currentPage}
                          </span>
                        )}

                      {currentPage < totalPages - 1 &&
                        currentPage !== totalPages && (
                          <span
                            className={styles.testListPaginationPageNumber}
                            onClick={() => goToPage(currentPage + 1)}
                            style={{ cursor: "pointer" }}
                          >
                            {currentPage + 1}
                          </span>
                        )}

                      {currentPage < totalPages - 2 && (
                        <span className={styles.testListPaginationEllipsis}>
                          ...
                        </span>
                      )}

                      {totalPages > 1 && (
                        <span
                          className={`${styles.testListPaginationPageNumber} ${
                            currentPage === totalPages ? styles.active : ""
                          }`}
                          onClick={() => goToPage(totalPages)}
                        >
                          {totalPages}
                        </span>
                      )}
                    </>
                  )}

                  <button
                    onClick={() => goToPage(currentPage + 1)}
                    disabled={currentPage === totalPages || totalPages === 0}
                    className={styles.testListPaginationButton}
                  >
                    <img src={chevronRight} alt="Next" />
                  </button>
                </div>
              </div>
            )}
          </div>
          <div className={styles.testListTable}>
            <table
              className={`${assignedTaskData ? styles.disabledTable : ""}`}
            >
              <thead>
                <tr>
                  <th>Exam Name</th>
                  <th>Subject</th>
                  <th>Grade</th>
                  <th>Exam Date</th>
                  <th></th>
                </tr>
              </thead>
              {currentData.length > 0 && (
                <tbody>
                  {currentData.map((examSession: any) => (
                    <tr key={examSession.examSessionId}>
                      <td>{examSession.examSessionName}</td>
                      <td>{examSession.subject}</td>
                      <td>{examSession.grade}</td>
                      <td>{examSession.examDate}</td>
                      <td
                        className={styles.testActions}
                        onClick={() =>
                          handleEvaluate(examSession.examSessionId)
                        }
                      >
                        evaluate
                      </td>
                    </tr>
                  ))}
                </tbody>
              )}
            </table>
          </div>
        </div>
      </div>
      {renderModals()}
    </div>
  );
}

export default EvaluatorDashboard;
